import type { ArchiveContents, RestoreSummary } from '@core/archive';
import { plural } from '@core/ui/RuDates';

/**
 * Тексты настроек — порт `SettingsFormatter`.
 *
 * Форматирования в вёрстке нет: так его можно проверить тестом, а не глазами.
 */

const KILO = 1024;
const TEN = 10;
const UNITS = ['КБ', 'МБ', 'ГБ', 'ТБ'];

/**
 * «412 МБ», «1,2 ГБ».
 *
 * Считаем по 1024, как показывает сама система: иначе цифра в приложении расходится
 * с цифрой в настройках телефона, и юрист не понимает, какой верить.
 */
export function size(bytes: number): string {
  if (bytes < KILO) return `${bytes} Б`;

  let value = bytes / KILO;
  let unit = 0;

  while (value >= KILO && unit < UNITS.length - 1) {
    value /= KILO;
    unit += 1;
  }

  const shown = value >= TEN ? String(Math.round(value)) : value.toFixed(1).replace('.', ',');
  return `${shown} ${UNITS[unit]}`;
}

/** «1,2 ГБ в 14 делах» либо «Документов пока нет». */
export const storageLine = (totalBytes: number, caseCount: number): string =>
  totalBytes === 0
    ? 'Документов пока нет'
    : `${size(totalBytes)} в ${plural(caseCount, 'деле', 'делах', 'делах')}`;

/** Сколько освободит уборка; `null`, когда убирать нечего. */
export const reclaimableLine = (bytes: number): string | null =>
  bytes <= 0 ? null : `Можно освободить ${size(bytes)}`;

/** «412 МБ · 87 документов» — строка тяжёлого дела. */
export const heavyCaseLine = (bytes: number, documents: number): string =>
  `${size(bytes)} · ${plural(documents, 'документ', 'документа', 'документов')}`;

const cases = (count: number) => plural(count, 'дело', 'дела', 'дел');
const documents = (count: number) => plural(count, 'документ', 'документа', 'документов');

/** «3 дела, 12 документов · 12,4 МБ» — что вышло в архиве и сколько он весит. */
export const archiveLine = (contents: ArchiveContents, bytes: number): string =>
  `${cases(contents.cases)}, ${documents(contents.documents)} · ${size(bytes)}`;

/**
 * «В архиве 3 дела, 12 документов, снят 08.09.2026» — что юрист увидит **до** загрузки.
 *
 * Дата обязательна: у человека на диске лежит несколько копий, и единственный способ
 * не развернуть позавчерашнюю — увидеть день, когда её сняли.
 */
export function pendingArchiveLine(contents: ArchiveContents, createdAt: number): string {
  const day = new Date(createdAt).toLocaleDateString('ru-RU');
  return `В архиве ${cases(contents.cases)}, ${documents(contents.documents)}, снят ${day}`;
}

/**
 * «Перенесено: 3 дела, 12 документов» либо «Всё это уже есть на устройстве».
 *
 * Ноль — не ошибка, а обычный исход повторной загрузки. Сказать об этом словами
 * дешевле, чем оставить юриста гадать, сработало ли.
 */
export const restoredLine = (summary: RestoreSummary): string =>
  summary.cases + summary.documents + summary.clients + summary.events === 0
    ? 'Всё это уже есть на устройстве'
    : `Перенесено: ${cases(summary.cases)}, ${documents(summary.documents)}`;

/**
 * Текст предупреждения о хранении — требование «Паспорта» §11.
 *
 * В вебе он **строже**, чем на Android: там приложение удаляют осознанно из меню,
 * а здесь иконку сносят одним долгим нажатием с домашнего экрана, и данные уходят
 * молча — ни корзины, ни копии в iCloud ([D-47](docs/DECISIONS.md)).
 *
 * Формулировка подлежит утверждению владельцем продукта: это заявление о судьбе
 * пользовательских данных, а не наша самодеятельность — [COMPLIANCE.md].
 */
export const STORAGE_WARNING =
  'Дела и документы хранятся только на этом устройстве. Если удалить иконку ' +
  'с домашнего экрана или очистить данные Safari, они пропадут навсегда — без корзины ' +
  'и без копии в iCloud.';

/**
 * Что говорим об архиве.
 *
 * Это прямое продолжение предупреждения выше: сказать, что данные пропадут, и не сказать,
 * как их сохранить, — половина правды. Формулировка касается судьбы данных юриста
 * и подлежит утверждению владельцем — [COMPLIANCE.md](docs/COMPLIANCE.md).
 */
export const ARCHIVE_HINT =
  'Архив — единственный способ унести дела с устройства и вернуть их обратно. ' +
  'Сохраните его в «Файлы», в облако или отправьте себе почтой.';

/**
 * Что говорим о статистике при первом запуске.
 *
 * **Черновик, требует утверждения владельцем** — это заявление о данных пользователя
 * ([D-23](docs/DECISIONS.md), [TELEMETRY.md]). В вебе статистика пока **не отправляется
 * вовсе**: она появится на шаге 20, и до тех пор текст говорит только о будущем.
 */
export const STATS_TEXT =
  'Считаем, сколько у вас дел и чем вы пользуетесь, чтобы понимать, что улучшать. ' +
  'Без названий, текстов и содержимого документов — они не покидают устройство.';
